import {action, computed, observable, makeObservable} from 'mobx';
import {Form} from '../../Form/stores/Form';
import {Search} from './Search';
import {serialize} from '../serializes/searchesList';
import {SEARCHES_MAX_NUM} from '../consts/searches';
import {texts, getLocalizedText} from '../../../constants/TextConst';
import {addSearchEntry} from '../../../services/indexDB/indexeddbServiceSearches';
import {isMacroActive} from '../../../_shared/GWTBridge/invoke/netForm';
export class SearchesStore {
    form: Form;
    filtersInitialized;
    @observable searchesList;
    @observable emptyState;
    @observable previousList;
    @observable formSelected;
    @observable generatorSelected: Search;
    @observable newSearch;
    @observable defaultIdx;

    constructor(form: Form, data, defaultSearch) {
        makeObservable(this);
        this.form = form;
        const {list, defaultIdx} = serialize(data);
        this.setList(list, defaultSearch);

        this.setDefault(defaultIdx);
        const search = this.searchesList[defaultSearch && defaultSearch.query];
        this.setFormSelected(search);
    }

    @computed get savedSearchesList() {
        return this.searchesList && this.searchesList.filter(search => search);
    }

    @computed get defaultSearch() {
        return this.searchesList && this.searchesList[this.defaultIdx];
    }

    @computed get getPreviousList() {
        return this.previousList;
    }

    @computed get emptyPlace() {
        const hole = this.searchesList.indexOf(undefined, 1);
        if (hole !== -1) {
            return hole;
        } else {
            return this.searchesList.length;
        }
    }

    @action setList(searches, defaultSearch) {
        this.searchesList = new Array(SEARCHES_MAX_NUM + 1);
        for (let i = 0; i < searches.length; i++) {
            const search = searches[i];
            const isDefault = defaultSearch && defaultSearch.query === search.serverIdx?.toString();
            const filters = isDefault ? defaultSearch.filters : null;
            const searchData = isDefault
                ? {...search, ...{ignoreCase: defaultSearch.ignoreCase}}
                : search;
            this.setSearch(new Search(this, searchData, filters));
        }
    }

    @action setSearch(search) {
        this.searchesList[search.serverIdx] = search;
    }

    getSearch(index) {
        return this.searchesList[index];
    }

    @action setPreviousList(searches) {
        this.previousList = [];
        for (let i = 0; i < searches.length; i++) {
            this.previousList.push(new Search(this, searches[i], searches[i].filters));
        }
    }

    @action setNewSearch(search) {
        this.newSearch = search;
    }

    @action setReorderedList(list, destinationIdx) {
        /* destinationIdx stands for new index in the "compressed" array
         * that comes from drag&drop component
         */
        const {defaultSearch} = this;
        /* prevIndex stands for previous index in the full array*/
        const prevIndex = list[destinationIdx].index;
        /* delete moved  search from the previous position in the full array */
        const moved = this.searchesList.splice(prevIndex, 1)[0];
        /* if the search isn't placed first, find out index (in the full list) of the search that comes before it */
        const previousSearchIndex =
            destinationIdx === 0 ? 0 : Number(list[destinationIdx - 1].index);
        /* put the moved search after it in the full array */
        this.searchesList.splice(previousSearchIndex + 1, 0, moved);
        // restore default and invoked search index according to the new index
        this.defaultIdx = defaultSearch && this.searchesList.indexOf(defaultSearch).toString();
    }

    @action setGeneratorSelected(search) {
        //create new instance insted of a reference
        this.generatorSelected = search ? new Search(this, search, search.filterValues) : search;

        //reset isDirty if we change the generatorSelected without exiting the panel (example: when we select another saved search inside the the panel)
        this.generatorSelected && this.generatorSelected.resetIsDirty();
    }

    @action setFormSelected(search) {
        const isRecon = this.form?.isRecon;
        this.formSelected = search;
        this.setGeneratorSelected(search);
        search &&
            !isRecon &&
            !search.isDefault &&
            search.filters?.size &&
            (search.isNew || !search.serverIdx) &&
            !isMacroActive() &&
            addSearchEntry(search);
    }

    @action setEmptyState(isEmpty) {
        this.emptyState = isEmpty;
    }

    @action setDefault(idx) {
        this.defaultIdx = idx;
    }

    toggleDefault(idx) {
        if (this.defaultIdx === idx) {
            this.setDefault(null);
        } else {
            this.setDefault(idx);
        }
    }

    getNextSearchAfterDelete(search) {
        const searchesList = this.savedSearchesList;
        const index = searchesList.indexOf(search);

        if (searchesList[index + 1]) {
            return searchesList[index + 1];
        } else {
            return searchesList[0];
        }
    }

    @action deleteSearch(search) {
        // if a new search is chosen in generator (== there's a new search)
        // and we're deleting an already existing search,
        // we move the its search to its place
        const index = this.searchesList.indexOf(search);
        this.searchesList[index] = undefined;
        if (search.isDefault) {
            this.toggleDefault(search.index);
        }

        let nextSearch;
        if (this.generatorSelected === search) {
            nextSearch = this.getNextSearchAfterDelete(search);

            if (!nextSearch) {
                this.addNewGeneratorSearch();
            } else {
                this.setGeneratorSelected(nextSearch);
            }
        }

        if (this.formSelected === search && !search.isNew) {
            this.formSelected.setIsNew(true);
            this.formSelected.setName(getLocalizedText(texts.newSearch));
            //TO DO What if we delete a search that is the current form selected
        }
    }

    @action deleteNewSearch() {
        this.newSearch = undefined;
    }

    setFiltersInitialized() {
        this.filtersInitialized = true;
    }

    @action addNewGeneratorSearch() {
        const newSearch = new Search(this);
        this.newSearch = newSearch;
        this.setGeneratorSelected(this.newSearch);
    }

    @action addNewFormSearch() {
        const newSearch = new Search(this);
        this.setFormSelected(newSearch);
    }

    @action saveNewSearch() {
        const newIndex = this.emptyPlace;
        this.newSearch.setIsNew(false);
        this.newSearch.setServerIdx(newIndex);
        this.searchesList[newIndex] = this.newSearch;
    }

    @action clearPreviousList() {
        this.previousList = [];
    }

    @action addSearch(item) {
        const newIndex = this.emptyPlace;
        item.setIsPrevious(false);
        item.setServerIdx(this.emptyPlace);
        this.searchesList[newIndex] = item;
        this.setGeneratorSelected(item);
    }

    @action resetToDefault() {
        this.setFormSelected(this.defaultSearch);
    }
}
